import clsx from 'clsx';
import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ShapeOption, SkinUndertoneModelOptions, buildSkinUndertoneOptions, skinUndertoneHelp, writeUserToStorage } from '@vestico/model';

import { FormValidationContext } from '../../CommonAppContext';
import { ShapeSelect } from '../../components/ShapeSelect';
import { useIsMounted } from '../../hooks';
import { StepProps } from '../../model/step';
import { UserContext } from '../../model/userContext';
import StepContainer from '../StepContainer';
import style from './UndertoneInput.module.css';

export const UndertoneInput = (props: StepProps) => {
  const { user } = useContext(UserContext);
  const { t } = useTranslation(['shared', 'model']);
  const formValidationContext = useContext(FormValidationContext);

  const [submitting, setSubmitting] = useState(false);
  useMemo(() => {
    const isSubmittingFromExternally = props.submitting;
    if (isSubmittingFromExternally) {
      setSubmitting(isSubmittingFromExternally);
    }
    return isSubmittingFromExternally;
  }, [props.submitting]);

  const skinUnderToneOptions = useMemo(() => buildSkinUndertoneOptions(t), [t]);

  const [skinUndertoneValue, setSkinUndertoneValue] = useState<number>(
    user?.skinUndertone !== undefined ? SkinUndertoneModelOptions.indexOf(user.skinUndertone) : 1
  );

  const helpText = useMemo(() => skinUndertoneHelp(SkinUndertoneModelOptions[skinUndertoneValue], t), [skinUndertoneValue, t]);

  const mounted = useIsMounted();
  const hipError = useMemo(
    () =>
      mounted.current && (skinUndertoneValue < 0 || skinUndertoneValue >= SkinUndertoneModelOptions.length)
        ? t('LOC_ENTER_INFO_STEP_ERROR_SKIN_UNDERTONE')
        : '',
    [mounted, skinUndertoneValue, t]
  );

  const formValid = useMemo(() => ![hipError].reduce((m, n) => m + n), [hipError]);

  useEffect(() => {
    if (!submitting) {
      return;
    }
    if (!user) {
      return;
    }

    formValidationContext.updateStep(props.path, true);

    user.skinUndertone = SkinUndertoneModelOptions[skinUndertoneValue];
    writeUserToStorage(user);

    setSubmitting(false);
  }, [submitting, skinUndertoneValue, formValid]);

  const inputProps = { shapeOptions: skinUnderToneOptions, selectedOptionIndex: skinUndertoneValue };

  const onShapeUpdate = useCallback((selectedShapeOption: ShapeOption) => {
    setSkinUndertoneValue(selectedShapeOption.shape);
  }, []);

  const onFormSubmit = useCallback((e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
  }, []);

  return (
    <StepContainer title={props.title}>
      <form className={clsx(style.formLayout)} onSubmit={onFormSubmit}>
        <ShapeSelect title="Skin Undertone" inputProps={inputProps} onUpdate={onShapeUpdate} />
        <div className={clsx(style.tip)}>
          <span style={{ fontWeight: 'bold' }}>Tip: </span>
          <span>{helpText}</span>
        </div>
      </form>
    </StepContainer>
  );
};
